import { useTranslation } from 'react-i18next';

import type { CatalogServiceCustomField } from '@/types';

import { Label } from '@/components/ui/label';

import { Input } from '../Input';
import { Select } from '../Select';
import { Textarea } from '../Textarea';

type ServiceCustomFieldsProps = {
  fields: CatalogServiceCustomField[];
  values: Record<number, string>;
  errors?: Record<string, string>;
  onChange: (fieldId: number, value: string) => void;
};

export const ServiceCustomFields = (props: ServiceCustomFieldsProps) => {
  const { fields, values, errors, onChange } = props;
  const { t } = useTranslation();

  if (fields.length === 0) {
    return null;
  }

  return (
    <div className="grid gap-3">
      <Label>{t('Custom fields')}</Label>

      {fields.map((field) => {
        const label = field.required ? `${field.label} *` : field.label;
        const value = values[field.id] ?? '';
        const error = errors?.[field.id];

        const control = () => {
          if (field.field_type === 'dropdown') {
            return (
              <Select
                label={label}
                value={value}
                onValueChange={(next) => onChange(field.id, next)}
                options={(field.option_values ?? []).map((option) => ({
                  id: option,
                  name: option,
                }))}
                isInvalid={!!error}
              />
            );
          }

          if (field.field_type === 'number') {
            return (
              <Input
                label={label}
                type="number"
                value={value}
                onChange={(event) => onChange(field.id, event.target.value)}
                isInvalid={!!error}
              />
            );
          }

          return (
            <Textarea
              label={label}
              value={value}
              onChange={(event) => onChange(field.id, event.target.value)}
              aria-invalid={!!error}
            />
          );
        };

        return (
          <div
            key={field.id}
            className="grid gap-1"
          >
            {control()}
            {!!error && <p className="text-destructive text-sm">{error}</p>}
          </div>
        );
      })}
    </div>
  );
};
